import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiTv, FiGrid, FiGlobe, FiTrendingUp } from 'react-icons/fi';
import { TopNavbar, NAV_ICON_SEARCH_INDEX, NAV_ICON_FAVORITES_INDEX, NAV_ICON_SETTINGS_INDEX, NAV_ICON_LOCK_INDEX, NAV_ICON_HISTORY_INDEX } from '../../components/TopNavbar/TopNavbar';
import { ContentNav } from '../../components/ContentNav/ContentNav';
import { getRuntimeTmdbLanguage, getRuntimeTmdbRegion } from '../../api/tmdbAPI';
import { getMatchedList } from '../../api/backendAPI';
import { consumeCatalogForceRefresh } from '../../utils/catalogRefresh';
import { useAuth } from '../../contexts/AuthContext';
import { useDevice } from '../../contexts/DeviceContext';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import { useLockToggle } from '../../hooks/useLockToggle';
import { useContentNavIds } from '../../hooks/useContentNavIds';
import { SeriesDetailsEntry } from '../SeriesDetails/SeriesDetails';
import PasswordModal from '../../components/PasswordModal/PasswordModal';
import HubLoadingMessage from '../../components/HubLoadingMessage/HubLoadingMessage';
import { useParentalContentLocks } from '../../hooks/useParentalContentLocks';
import './Series.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SeriesItem {
  id: string;
  title?: string;
  posterUrl?: string;
  backdropUrl?: string;
  seriesStreamId?: string;
  categoryId?: string;
}

export type { SeriesDetailsEntry };

// ─── Cache ────────────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function cacheKey(username: string, language: string) {
  return `ott_series_v1_${language}_${username}`;
}

function loadCache(username: string, language: string): { slider: SeriesItem[]; posters: SeriesItem[] } | null {
  try {
    const raw = localStorage.getItem(cacheKey(username, language));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: { slider: SeriesItem[]; posters: SeriesItem[] }; ts: number };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function saveCache(username: string, language: string, data: { slider: SeriesItem[]; posters: SeriesItem[] }) {
  try {
    localStorage.setItem(cacheKey(username, language), JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

// ─── Category definitions ─────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'top',      label: 'Top',       Icon: FiTrendingUp, color: '#3d1a5a', accent: '#7b2fbf' },
  { id: 'national', label: 'Nacional',  Icon: FiGlobe,  color: '#5a1040', accent: '#8b1a6a' },
  { id: 'new',      label: 'Novidades', Icon: FiTv,     color: '#0d3325', accent: '#1a6b4a' },
  { id: 'all',      label: 'Todos',     Icon: FiGrid,   color: '#0d3352', accent: '#1565c0' },
] as const;

// ─── Small poster card ────────────────────────────────────────────────────────

interface SmallPosterProps {
  item?: SeriesItem;
  loading?: boolean;
  focused?: boolean;
  onClick?: () => void;
}

const SmallPoster: React.FC<SmallPosterProps> = ({ item, loading, focused, onClick }) => {
  const style: React.CSSProperties = item?.posterUrl
    ? { backgroundImage: `url(${item.posterUrl})` }
    : {};
  return (
    <div
      className={[
        'series__poster',
        loading ? 'series__poster--loading' : '',
        focused ? 'series__poster--focused' : '',
      ].filter(Boolean).join(' ')}
      style={style}
      onClick={onClick}
    >
      {item?.title && (
        <div className="series__poster-title">{item.title}</div>
      )}
    </div>
  );
};

// ─── Category button ──────────────────────────────────────────────────────────

interface CategoryBtnProps {
  label: string;
  Icon: React.ComponentType<{ size?: number }>;
  color: string;
  accent: string;
  focused: boolean;
  onClick?: () => void;
}

const CategoryBtn: React.FC<CategoryBtnProps> = ({ label, Icon, focused, onClick }) => (
  <div
    className={`series__cat-btn${focused ? ' series__cat-btn--focused' : ''}`}
    onClick={onClick}
  >
    <Icon size={28} />
    <span className="series__cat-label">{label}</span>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface SeriesProps {
  isActive?: boolean;
  onBack?: () => void;
  onNavigate?: (sectionId: string) => void;
  onOpenSeriesDetails?: (entry: SeriesDetailsEntry) => void;
  onOpenSettings?: () => void;
  onOpenSearch?: () => void;
  onOpenFavorites?: () => void;
  onOpenHistory?: () => void;
}

type FocusSection = 'topnav' | 'contentnav' | 'slider' | 'categories' | 'posters';
const NAV_ICON_COUNT_S = 5;

export const Series: React.FC<SeriesProps> = ({
  isActive = true,
  onBack,
  onNavigate,
  onOpenSeriesDetails,
  onOpenSettings,
  onOpenSearch,
  onOpenFavorites,
  onOpenHistory,
}) => {
  const { t, i18n } = useTranslation();
  const { isBlocked } = useParentalControls();
  const {
    handleLockIconClick,
    handleLockPasswordConfirm,
    handleLockPasswordCancel,
    lockModalOpen,
    lockModalMode,
    lockModalError,
  } = useLockToggle();
  const CAT_LABEL_KEY: Record<string, string> = {
    top: 'series.top',
    national: 'series.national',
    new: 'series.new',
    all: 'series.all',
  };
  const { authData } = useAuth();
  const { isContentRestricted } = useParentalContentLocks();
  const { deviceId, activationPassword } = useDevice();
  const [allSlider, setSlider] = useState<SeriesItem[]>([]);
  const [allPosters, setPosters] = useState<SeriesItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sliderIdx, setSliderIdx] = useState(0);
  const [focusSection, setFocusSection] = useState<FocusSection>('slider');
  const [focusCat, setFocusCat] = useState(0);
  const [focusPoster, setFocusPoster] = useState(0);
  const [topnavFocusedIdx, setTopnavFocusedIdx] = useState(0);
  const [contentnavFocusedId, setContentnavFocusedId] = useState('series');
  const contentNavIds = useContentNavIds();
  const autoAdvRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Data fetching ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const username = authData?.user?.username ?? 'anon';
    const tmdbLanguage = getRuntimeTmdbLanguage(i18n.language);
    const tmdbRegion = getRuntimeTmdbRegion(i18n.language);

    const cached = loadCache(username, tmdbLanguage);
    if (cached) {
      setSlider(cached.slider);
      setPosters(cached.posters);
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        const toItem = (m: { tmdbId: number; title: string; posterUrl: string | null; backdropUrl: string | null; streamId: string; categoryId?: string }): SeriesItem => ({
          id: String(m.tmdbId),
          title: m.title,
          posterUrl: m.posterUrl ?? undefined,
          backdropUrl: m.backdropUrl ?? undefined,
          seriesStreamId: m.streamId,
          categoryId: m.categoryId,
        });

        let sliderItems: SeriesItem[] = [];
        let posterItems: SeriesItem[] = [];

        if (!authData) {
          // No credentials — nothing to show
          sliderItems = [];
          posterItems = [];
        } else {
          const items = await getMatchedList(deviceId, activationPassword, 'popular-series', tmdbLanguage, tmdbRegion, 7, 'preview', consumeCatalogForceRefresh('popular-series'));
          if (cancelled) return;
          sliderItems = items.slice(0, 5).map(toItem);
          posterItems = items.slice(5, 7).map(toItem);
        }

        if (!cancelled) {
          setSlider(sliderItems);
          setPosters(posterItems);
          setLoading(false);
          saveCache(username, tmdbLanguage, { slider: sliderItems, posters: posterItems });
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [authData, deviceId, activationPassword, i18n.language]);

  const slider = useMemo(
    () => allSlider.filter((item) => !isContentRestricted('series', item.seriesStreamId, item.categoryId)),
    [allSlider, isContentRestricted]
  );
  const posters = useMemo(
    () => allPosters.filter((item) => !isContentRestricted('series', item.seriesStreamId, item.categoryId)),
    [allPosters, isContentRestricted]
  );

  useEffect(() => {
    setSliderIdx((index) => Math.min(index, Math.max(0, slider.length - 1)));
    setFocusPoster((index) => Math.min(index, Math.max(0, posters.length - 1)));
  }, [posters.length, slider.length]);

  // ─── Slider auto-advance ──────────────────────────────────────────────────
  useEffect(() => {
    if (loading || !slider.length) return;
    autoAdvRef.current = setInterval(
      () => setSliderIdx((prev) => (prev + 1) % slider.length),
      5000,
    );
    return () => { if (autoAdvRef.current) clearInterval(autoAdvRef.current); };
  }, [loading, slider.length]);

  const goToSlide = useCallback(
    (idx: number) => {
      if (autoAdvRef.current) { clearInterval(autoAdvRef.current); autoAdvRef.current = null; }
      setSliderIdx(idx);
      if (slider.length) {
        autoAdvRef.current = setInterval(
          () => setSliderIdx((prev) => (prev + 1) % slider.length),
          5000,
        );
      }
    },
    [slider.length],
  );

  // ─── Open details ─────────────────────────────────────────────────────────
  const openItem = useCallback(
    (item?: SeriesItem) => {
      if (!item) return;
      onOpenSeriesDetails?.({
        tmdbId: Number(item.id),
        title: item.title,
        posterUrl: item.posterUrl,
        backdropUrl: item.backdropUrl,
        seriesStreamId: item.seriesStreamId,
        categoryId: item.categoryId,
      });
    },
    [onOpenSeriesDetails],
  );

  // ─── Keyboard navigation ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
        case 'GoBack':
        case 'BrowserBack':
          e.preventDefault();
          onBack?.();
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx((prev) => Math.max(0, prev - 1));
          } else if (focusSection === 'contentnav') {
            const idx = contentNavIds.indexOf(contentnavFocusedId);
            if (idx > 0) setContentnavFocusedId(contentNavIds[idx - 1]);
          } else if (focusSection === 'slider') {
            goToSlide((sliderIdx - 1 + (slider.length || 1)) % (slider.length || 1));
          } else if (focusSection === 'categories') {
            setFocusCat((prev) => Math.max(0, prev - 1));
          } else if (focusSection === 'posters') {
            if (focusPoster === 0) {
              setFocusSection('categories');
            } else {
              setFocusPoster((prev) => Math.max(0, prev - 1));
            }
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx((prev) => Math.min(NAV_ICON_COUNT_S - 1, prev + 1));
          } else if (focusSection === 'contentnav') {
            const idx = contentNavIds.indexOf(contentnavFocusedId);
            if (idx < contentNavIds.length - 1) setContentnavFocusedId(contentNavIds[idx + 1]);
          } else if (focusSection === 'slider') {
            if (sliderIdx === slider.length - 1) {
              setFocusSection('posters');
              setFocusPoster(0);
            } else {
              goToSlide(sliderIdx + 1);
            }
          } else if (focusSection === 'categories') {
            if (focusCat < CATEGORIES.length - 1) {
              setFocusCat((prev) => prev + 1);
            } else {
              setFocusSection('posters');
              setFocusPoster(0);
            }
          } else if (focusSection === 'posters') {
            setFocusPoster((prev) => Math.min(1, prev + 1));
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setFocusSection('contentnav');
          } else if (focusSection === 'contentnav') {
            setFocusSection('slider');
          } else if (focusSection === 'slider') {
            setFocusSection('categories');
            setFocusCat(0);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (focusSection === 'contentnav') {
            setFocusSection('topnav');
          } else if (focusSection === 'slider') {
            setFocusSection('contentnav');
          } else if (focusSection === 'categories') {
            setFocusSection('slider');
          } else if (focusSection === 'posters') {
            setFocusSection('contentnav');
          }
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusSection === 'topnav') {
            if (topnavFocusedIdx === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
            if (topnavFocusedIdx === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
            if (topnavFocusedIdx === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
            if (topnavFocusedIdx === NAV_ICON_LOCK_INDEX) handleLockIconClick();
            if (topnavFocusedIdx === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
          } else if (focusSection === 'contentnav') {
            if (contentnavFocusedId === 'series') {
              setFocusSection('slider');
            } else {
              onNavigate?.(contentnavFocusedId);
            }
          } else if (focusSection === 'slider') {
            openItem(slider[sliderIdx]);
          } else if (focusSection === 'posters') {
            openItem(posters[focusPoster]);
          } else if (focusSection === 'categories') {
            onNavigate?.(`series-${CATEGORIES[focusCat].id}`);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isActive, focusSection, focusCat, focusPoster, sliderIdx,
    slider, posters, goToSlide, onBack, openItem, onNavigate,
    topnavFocusedIdx, contentnavFocusedId, contentNavIds, handleLockIconClick, onOpenSearch, onOpenFavorites, onOpenSettings, onOpenHistory,
  ]);

  // ─── Render ───────────────────────────────────────────────────────────────
  const currentSlide = slider[sliderIdx];
  const sliderStyle: React.CSSProperties = currentSlide?.backdropUrl
    ? { backgroundImage: `url(${currentSlide.backdropUrl})` }
    : currentSlide?.posterUrl
    ? { backgroundImage: `url(${currentSlide.posterUrl})` }
    : {};

  if (isBlocked('series')) {
    return (
      <div className="series series--blocked">
        <TopNavbar
          focusedIndex={focusSection === 'topnav' ? topnavFocusedIdx : undefined}
          onIconClick={(i) => {
            if (i === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
            if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
            if (i === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
            if (i === NAV_ICON_LOCK_INDEX) handleLockIconClick();
            if (i === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
          }}
        />
        <div className="series__blocked-msg">
          <p>{t('parental.blockedMessage')}</p>
        </div>
        {lockModalOpen && (
          <PasswordModal
            mode={lockModalMode}
            error={lockModalError ? t(lockModalError) : undefined}
            onConfirm={handleLockPasswordConfirm}
            onCancel={handleLockPasswordCancel}
          />
        )}
      </div>
    );
  }

  return (
    <>
    <div className="series">
      <TopNavbar
        focusedIndex={focusSection === 'topnav' ? topnavFocusedIdx : undefined}
        onIconClick={(i) => {
          if (i === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
          if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
          if (i === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
          if (i === NAV_ICON_LOCK_INDEX) handleLockIconClick();
          if (i === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
        }}
      />
      <ContentNav
        activeId="series"
        focusedId={focusSection === 'contentnav' ? contentnavFocusedId : undefined}
        onSelect={(id) => onNavigate?.(id)}
      />

      <div className="series__main">
        {/* ── Left column ──────────────────────────────────── */}
        <div className="series__left">
          {/* Slider */}
          <div
            className={[
              'series__slider',
              loading ? 'series__slider--loading' : '',
              focusSection === 'slider' ? 'series__slider--focused' : '',
            ].filter(Boolean).join(' ')}
            style={sliderStyle}
            onClick={() => { setFocusSection('slider'); openItem(currentSlide); }}
          >
            {!loading && (
              <>
                <div className="series__slider-gradient" />
                {currentSlide?.title && (
                  <div className="series__slider-title">{currentSlide.title}</div>
                )}
                <div className="series__slider-dots">
                  {slider.map((_, i) => (
                    <div
                      key={i}
                      className={`series__slider-dot${i === sliderIdx ? ' series__slider-dot--active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); goToSlide(i); }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Categories */}
          <div className="series__categories">
            {CATEGORIES.map((cat, i) => (
              <CategoryBtn
                key={cat.id}
                label={t(CAT_LABEL_KEY[cat.id])}
                Icon={cat.Icon}
                color={cat.color}
                accent={cat.accent}
                focused={focusSection === 'categories' && focusCat === i}
                onClick={() => {
                  setFocusSection('categories');
                  setFocusCat(i);
                  onNavigate?.(`series-${cat.id}`);
                }}
              />
            ))}
          </div>
        </div>

        {/* ── Right column — 2 portrait posters ──────────── */}
        <div className="series__right">
          <SmallPoster
            item={posters[0]}
            loading={loading}
            focused={focusSection === 'posters' && focusPoster === 0}
            onClick={() => { setFocusSection('posters'); setFocusPoster(0); openItem(posters[0]); }}
          />
          <SmallPoster
            item={posters[1]}
            loading={loading}
            focused={focusSection === 'posters' && focusPoster === 1}
            onClick={() => { setFocusSection('posters'); setFocusPoster(1); openItem(posters[1]); }}
          />
        </div>
      </div>
      <HubLoadingMessage active={loading} scope={`series:${authData?.user?.username ?? 'anon'}`} />
    </div>
    {lockModalOpen && (
      <PasswordModal
        mode={lockModalMode}
        error={lockModalError ? t(lockModalError) : undefined}
        onConfirm={handleLockPasswordConfirm}
        onCancel={handleLockPasswordCancel}
      />
    )}
    </>
  );
};
